import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Users, CalendarDays, Newspaper, ShieldAlert, Plus, Trash2, MapPin, Ban, CheckCircle2, UserCog, Crown, Download, User, Upload, Image as ImageIcon, Camera } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { api, fileUrl, mediaUrl } from "@/lib/api";
import { LGAS, ROLE_LABELS, NATIONAL_EXECUTIVES, ALL_EXCO_ROLES, ETHNIC_GROUPS } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

async function downloadCsv(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function ImageUpload({ value, onChange, testid = "image-upload-input" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <Label>Image (optional)</Label>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={up} data-testid={testid} />
      <div className="mt-2 flex items-center gap-3">
        <div className="w-16 h-16 rounded-lg bg-[#0B0C0E] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-zinc-600" />}
        </div>
        <Button type="button" variant="outline" className="border-white/15 bg-transparent" disabled={busy} onClick={() => ref.current?.click()}>
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} {value ? "Change Image" : "Upload Image"}
        </Button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {  return (
    <div className="rounded-2xl border border-white/10 bg-[#131519] p-6">
      <div className="flex items-center justify-between">
        <div><div className="text-3xl font-heading font-extrabold">{value}</div><div className="text-sm text-zinc-400 mt-1">{label}</div></div>
        <div className="w-11 h-11 rounded-xl bg-[#E6197E]/15 flex items-center justify-center"><Icon className="w-5 h-5 text-[#E6197E]" /></div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => { if (user === false) navigate("/login"); }, [user, navigate]);

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user) loadStats(); }, [user, loadStats]);

  if (!user) return <div className="pt-40 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E6197E]" /></div>;

  const isSuper = user.role === "super_admin";
  const isNational = user.role === "national_ict_lead";
  const isLga = user.role === "lga_ict_lead";
  const canManageExco = isSuper || (isNational && user.data_access);

  return (
    <main className="pt-24 pb-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline-label mb-2">{ROLE_LABELS[user.role]} {isLga && `· ${user.lga} LGA`}</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">Dashboard</h1>
          </div>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            <StatCard icon={Users} label={isLga ? `Members in ${user.lga}` : "Total Members"} value={stats.total_members} />
            <StatCard icon={CalendarDays} label="Events" value={stats.total_events} />
            <StatCard icon={Newspaper} label="News Posts" value={stats.total_news} />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-[#131519] border border-white/10 flex-wrap h-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news">News</TabsTrigger>
            <TabsTrigger value="cultural" data-testid="tab-cultural">Nasarawa @30</TabsTrigger>
            <TabsTrigger value="checkin" data-testid="tab-checkin">Check-In</TabsTrigger>
            {isSuper && <TabsTrigger value="users" data-testid="tab-users">Users & Access</TabsTrigger>}
            {canManageExco && <TabsTrigger value="exco" data-testid="tab-exco">Leadership</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview" className="mt-8"><Overview stats={stats} /></TabsContent>
          <TabsContent value="members" className="mt-8"><MembersPanel user={user} /></TabsContent>
          <TabsContent value="events" className="mt-8"><EventsPanel user={user} onChange={loadStats} /></TabsContent>
          <TabsContent value="news" className="mt-8"><NewsPanel user={user} onChange={loadStats} /></TabsContent>
          <TabsContent value="cultural" className="mt-8"><CulturalPanel user={user} /></TabsContent>
          <TabsContent value="checkin" className="mt-8"><CheckinPanel /></TabsContent>
          {isSuper && <TabsContent value="users" className="mt-8"><UsersPanel /></TabsContent>}
          {canManageExco && <TabsContent value="exco" className="mt-8"><ExcoPanel isSuper={isSuper} /></TabsContent>}
        </Tabs>
      </div>
    </main>
  );
}

function Overview({ stats }) {
  if (!stats) return null;
  const max = Math.max(1, ...stats.per_lga.map((p) => p.count));
  return (
    <div className="rounded-2xl border border-white/10 bg-[#131519] p-6">
      <h3 className="font-heading text-lg font-semibold mb-6">Members by Local Government Area</h3>
      <div className="space-y-3">
        {stats.per_lga.map((p) => (
          <div key={p.lga} className="flex items-center gap-4">
            <div className="w-32 text-sm text-zinc-300 shrink-0">{p.lga}</div>
            <div className="flex-1 h-3 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#E6197E] rounded-full" style={{ width: `${(p.count / max) * 100}%` }} /></div>
            <div className="w-8 text-right text-sm font-mono-accent">{p.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MembersPanel({ user }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lga, setLga] = useState("all");

  useEffect(() => {
    setLoading(true); setError(null);
    const params = user.role === "lga_ict_lead" ? {} : (lga !== "all" ? { lga } : {});
    api.get("/admin/members", { params }).then((r) => setMembers(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [lga, user.role]);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#E6197E]" /></div>;
  if (error) return <div className="rounded-2xl border border-red-500/30 bg-red-950/30 p-8 text-center" data-testid="members-access-denied"><Ban className="w-8 h-8 text-red-400 mx-auto mb-3" /><p className="text-red-300">{error}</p></div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        {user.role !== "lga_ict_lead" ? (
          <div className="max-w-xs w-full">
            <Select value={lga} onValueChange={setLga}>
              <SelectTrigger className="bg-[#131519] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All LGAs</SelectItem>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ) : <div />}
        <Button onClick={() => downloadCsv("/admin/members/export", "nasarawa-members.csv")} variant="outline" size="sm" className="border-white/15 bg-transparent" data-testid="export-members-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#131519] overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
            <TableHead>Member</TableHead><TableHead>ID</TableHead><TableHead>LGA</TableHead><TableHead>Discipline</TableHead><TableHead>Expertise</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {members.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-zinc-500 py-10">No members yet.</TableCell></TableRow> :
              members.map((m) => (
                <TableRow key={m.id} className="border-white/5" data-testid="admin-member-row">
                  <TableCell className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-zinc-800 overflow-hidden shrink-0">{m.photo_path && <img src={fileUrl(m.photo_path)} alt="" className="w-full h-full object-cover" />}</div>
                    <span className="font-medium">{m.full_name}</span>
                  </TableCell>
                  <TableCell className="font-mono-accent text-xs text-[#E6197E]">{m.membership_id}</TableCell>
                  <TableCell>{m.lga}</TableCell><TableCell className="text-zinc-400 text-sm">{m.discipline}</TableCell>
                  <TableCell className="text-sm">{m.expertise}</TableCell><TableCell className="text-sm text-zinc-400">{m.phone}</TableCell>
                  <TableCell className="text-sm text-zinc-400">{m.email || "—"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function EventsPanel({ user, onChange }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [regs, setRegs] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", date: "", location: "", scope: user.role === "national_ict_lead" ? "national" : user.role === "lga_ict_lead" ? "lga" : "national", lga: user.lga || "", image: "" });

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.date || !form.location) return toast.error("Fill title, date and location");
    try {
      await api.post("/events", form);
      toast.success("Event created");
      setOpen(false); setForm({ ...form, title: "", description: "", date: "", location: "", image: "" });
      load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Event deleted"); load(); onChange(); };
  const viewRegs = async (ev) => { const { data } = await api.get("/admin/event-registrations", { params: { event_id: ev.id } }); setRegs({ ev, data }); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h3 className="font-heading text-lg font-semibold">Manage Events</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="admin-create-event-button"><Plus className="w-4 h-4 mr-2" /> New Event</Button></DialogTrigger>
          <DialogContent className="bg-[#131519] border-white/10 max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Create Event</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="event-title-input" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" /></div>
              </div>
              <ImageUpload value={form.image} onChange={(p) => setForm({ ...form, image: p })} testid="image-upload-input" />
              {user.role === "super_admin" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Scope</Label>
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                      <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="national">National</SelectItem><SelectItem value="lga">LGA</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {form.scope === "lga" && <div><Label>LGA</Label>
                    <Select value={form.lga} onValueChange={(v) => setForm({ ...form, lga: v })}>
                      <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select></div>}
                </div>
              )}
              <Button onClick={create} className="w-full bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="event-save-button">Create Event</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.length === 0 ? <p className="text-zinc-500">No events yet.</p> : events.map((ev) => (
          <div key={ev.id} className="rounded-2xl border border-white/10 bg-[#131519] p-5">
            <div className="flex justify-between items-start">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${ev.scope === "national" ? "bg-[#E6197E]/20 text-[#E6197E]" : "bg-amber-600/20 text-amber-400"}`}>{ev.scope === "national" ? "National" : ev.lga}</span>
              <button onClick={() => del(ev.id)} className="text-zinc-500 hover:text-red-400" data-testid="event-delete-button"><Trash2 className="w-4 h-4" /></button>
            </div>
            <h4 className="font-heading font-semibold mt-3">{ev.title}</h4>
            <div className="text-xs text-zinc-400 mt-2 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5" />{ev.date} · <MapPin className="w-3.5 h-3.5" />{ev.location}</div>
            <Button onClick={() => viewRegs(ev)} variant="outline" size="sm" className="mt-4 border-white/15 bg-transparent w-full"><Users className="w-4 h-4 mr-2" />{ev.registration_count} Registrations</Button>
          </div>
        ))}
      </div>

      <Dialog open={!!regs} onOpenChange={() => setRegs(null)}>
        <DialogContent className="bg-[#131519] border-white/10 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Registrations · {regs?.ev.title}</DialogTitle></DialogHeader>
          {regs && <div className="flex justify-end"><Button onClick={() => downloadCsv(`/admin/event-registrations/export?event_id=${regs.ev.id}`, "event-registrations.csv")} variant="outline" size="sm" className="border-white/15 bg-transparent" data-testid="export-regs-button"><Download className="w-4 h-4 mr-2" /> Export CSV</Button></div>}
          {regs?.data.length === 0 ? <p className="text-zinc-500 py-6 text-center">No registrations yet.</p> : (
            <Table><TableHeader><TableRow className="border-white/10"><TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>LGA</TableHead></TableRow></TableHeader>
              <TableBody>{regs?.data.map((r) => <TableRow key={r.id} className="border-white/5"><TableCell>{r.name}</TableCell><TableCell className="text-zinc-400">{r.phone}</TableCell><TableCell>{r.lga}</TableCell></TableRow>)}</TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewsPanel({ user, onChange }) {
  const [news, setNews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", scope: user.role === "national_ict_lead" ? "national" : user.role === "lga_ict_lead" ? "lga" : "national", lga: user.lga || "", image: "" });

  const load = () => api.get("/news").then((r) => setNews(r.data));
  useEffect(() => { load(); }, []);

  const mine = user.role === "super_admin" ? news : news.filter((n) => n.author_id === user.id || (user.role === "national_ict_lead" && n.scope === "national") || (user.role === "lga_ict_lead" && n.lga === user.lga));

  const create = async () => {
    if (!form.title || !form.content) return toast.error("Title and content required");
    try {
      await api.post("/news", form);
      toast.success("News published");
      setOpen(false); setForm({ ...form, title: "", content: "", image: "" });
      load(); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/news/${id}`); toast.success("Deleted"); load(); onChange(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h3 className="font-heading text-lg font-semibold">{user.role === "lga_ict_lead" ? `${user.lga} News` : "Manage News"}</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="admin-post-news-button"><Plus className="w-4 h-4 mr-2" /> Post News</Button></DialogTrigger>
          <DialogContent className="bg-[#131519] border-white/10 max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Publish News</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="news-title-input" /></div>
              <div><Label>Content</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10 min-h-[120px]" data-testid="news-content-input" /></div>
              <ImageUpload value={form.image} onChange={(p) => setForm({ ...form, image: p })} testid="image-upload-input" />
              {user.role === "super_admin" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Scope</Label>
                    <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                      <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="national">National</SelectItem><SelectItem value="lga">LGA</SelectItem></SelectContent>
                    </Select>
                  </div>
                  {form.scope === "lga" && <div><Label>LGA</Label>
                    <Select value={form.lga} onValueChange={(v) => setForm({ ...form, lga: v })}>
                      <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select></div>}
                </div>
              )}
              {user.role === "lga_ict_lead" && <p className="text-xs text-zinc-500">This will publish under the <span className="text-[#E6197E]">{user.lga}</span> LGA section.</p>}
              <Button onClick={create} className="w-full bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="news-save-button">Publish</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {mine.length === 0 ? <p className="text-zinc-500">No news posts yet.</p> : mine.map((n) => (
          <div key={n.id} className="rounded-xl border border-white/10 bg-[#131519] p-4 flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${n.scope === "national" ? "bg-[#E6197E]/20 text-[#E6197E]" : "bg-amber-600/20 text-amber-400"}`}>{n.scope === "national" ? "National" : n.lga}</span><span className="text-[11px] text-zinc-500">{new Date(n.created_at).toLocaleDateString()}</span></div>
              <h4 className="font-heading font-semibold">{n.title}</h4>
              <p className="text-sm text-zinc-400 mt-1 line-clamp-2">{n.content}</p>
            </div>
            <button onClick={() => del(n.id)} className="text-zinc-500 hover:text-red-400 shrink-0" data-testid="news-delete-button"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "lga_ict_lead", lga: "" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Fill all fields");
    if (form.role === "lga_ict_lead" && !form.lga) return toast.error("Select an LGA");
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm({ name: "", email: "", password: "", role: "lga_ict_lead", lga: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAccess = async (u) => {
    try { await api.patch(`/users/${u.id}`, { data_access: !u.data_access }); toast.success(`Data access ${!u.data_access ? "granted" : "restricted"} for ${u.name}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeRole = async (u, role) => {
    try { await api.patch(`/users/${u.id}`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (u) => { try { await api.delete(`/users/${u.id}`); toast.success("User removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div><h3 className="font-heading text-lg font-semibold flex items-center gap-2"><UserCog className="w-5 h-5 text-[#E6197E]" /> Users & Data Access</h3><p className="text-sm text-zinc-400 mt-1">Assign roles and grant or restrict access to member & registration data.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="admin-add-user-button"><Plus className="w-4 h-4 mr-2" /> Add User</Button></DialogTrigger>
          <DialogContent className="bg-[#131519] border-white/10">
            <DialogHeader><DialogTitle className="font-heading">Create Staff User</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="user-name-input" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="user-email-input" /></div>
              <div><Label>Password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="user-password-input" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="admin-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="national_ict_lead">National ICT Lead</SelectItem><SelectItem value="lga_ict_lead">LGA ICT Lead</SelectItem></SelectContent>
                </Select>
              </div>
              {form.role === "lga_ict_lead" && <div><Label>LGA</Label>
                <Select value={form.lga} onValueChange={(v) => setForm({ ...form, lga: v })}>
                  <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10"><SelectValue placeholder="Select LGA" /></SelectTrigger>
                  <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select></div>}
              <Button onClick={create} className="w-full bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="user-save-button">Create User</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#131519] overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead>User</TableHead><TableHead>Role</TableHead><TableHead>LGA</TableHead><TableHead>Data Access</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className="border-white/5" data-testid="admin-user-row">
                <TableCell><div className="font-medium">{u.name}</div><div className="text-xs text-zinc-500">{u.email}</div></TableCell>
                <TableCell>
                  {u.role === "super_admin" ? <span className="flex items-center gap-1.5 text-[#E6197E] font-medium text-sm"><Crown className="w-4 h-4" /> Super Admin</span> : (
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="bg-[#0B0C0E] border-white/10 h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="national_ict_lead">National ICT Lead</SelectItem><SelectItem value="lga_ict_lead">LGA ICT Lead</SelectItem></SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell className="text-sm text-zinc-400">{u.lga || "—"}</TableCell>
                <TableCell>
                  {u.role === "super_admin" ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : (
                    <div className="flex items-center gap-2" data-testid="admin-toggle-data-access-button">
                      <Switch checked={!!u.data_access} onCheckedChange={() => toggleAccess(u)} />
                      <span className={`text-xs ${u.data_access ? "text-emerald-400" : "text-zinc-500"}`}>{u.data_access ? "Granted" : "Restricted"}</span>
                    </div>
                  )}
                </TableCell>
                <TableCell>{u.role !== "super_admin" && <button onClick={() => del(u)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function CulturalPanel({ user }) {
  const [vols, setVols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lga, setLga] = useState("all");
  const [ethnic, setEthnic] = useState("all");

  useEffect(() => {
    setLoading(true); setError(null);
    const params = {};
    if (user.role !== "lga_ict_lead" && lga !== "all") params.lga = lga;
    if (ethnic !== "all") params.ethnic_group = ethnic;
    api.get("/admin/cultural-runway", { params }).then((r) => setVols(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [lga, ethnic, user.role]);

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#E6197E]" /></div>;
  if (error) return <div className="rounded-2xl border border-red-500/30 bg-red-950/30 p-8 text-center"><Ban className="w-8 h-8 text-red-400 mx-auto mb-3" /><p className="text-red-300">{error}</p></div>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Crown className="w-5 h-5 text-[#F5A623]" /> Nasarawa @30 Cultural Runway</h3>
          <p className="text-sm text-zinc-400 mt-1">{vols.length} volunteer{vols.length !== 1 ? "s" : ""} ready to showcase heritage attire.</p>
        </div>
        <Button onClick={() => downloadCsv("/admin/cultural-runway/export", "cultural-runway-volunteers.csv")} variant="outline" size="sm" className="border-white/15 bg-transparent" data-testid="export-cultural-button">
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        {user.role !== "lga_ict_lead" && (
          <Select value={lga} onValueChange={setLga}>
            <SelectTrigger className="bg-[#131519] border-white/10 max-w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All LGAs</SelectItem>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select value={ethnic} onValueChange={setEthnic}>
          <SelectTrigger className="bg-[#131519] border-white/10 max-w-[240px]"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-64"><SelectItem value="all">All Ethnic Groups</SelectItem>{ETHNIC_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#131519] overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
            <TableHead>Name</TableHead><TableHead>Ethnic Group / Attire</TableHead><TableHead>Gender</TableHead><TableHead>LGA</TableHead><TableHead>Phone</TableHead><TableHead>Notes</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {vols.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-10">No volunteers yet.</TableCell></TableRow> :
              vols.map((v) => (
                <TableRow key={v.id} className="border-white/5" data-testid="cultural-volunteer-row">
                  <TableCell className="font-medium">{v.full_name}</TableCell>
                  <TableCell className="text-[#F5A623]">{v.ethnic_group}</TableCell>
                  <TableCell className="text-sm">{v.gender}</TableCell>
                  <TableCell>{v.lga}</TableCell>
                  <TableCell className="text-sm text-zinc-400">{v.phone}</TableCell>
                  <TableCell className="text-sm text-zinc-400 max-w-[220px] truncate">{v.notes || "—"}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function QrScanner({ onResult, onClose }) {
  const scannerRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    let started = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        if (cancelled) return;
        const scanner = new Html5Qrcode("qr-reader-region");
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
          (text) => onResult(text), () => {});
        started = true;
        if (cancelled) { try { await scanner.stop(); scanner.clear(); } catch { /* noop */ } }
      } catch (e) {
        toast.error("Could not access camera. Type the ticket ID instead.");
        onClose?.();
      }
    })();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s && started) { s.stop().then(() => s.clear()).catch(() => {}); }
    };
  }, []);
  return <div id="qr-reader-region" className="w-full rounded-xl overflow-hidden bg-black [&_video]:rounded-xl" data-testid="qr-reader-region" />;
}

function CheckinPanel() {
  const [tid, setTid] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);

  const check = async (idArg) => {
    const id = (typeof idArg === "string" ? idArg : tid).trim();
    if (!id) return toast.error("Enter a ticket ID");
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${id}/checkin`);
      setResult(data);
      if (data.status === "ok") toast.success("Checked in!");
      else toast("Already checked in earlier");
    } catch (e) { setResult({ status: "notfound" }); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const parseTid = (t) => { const s = (t || "").trim(); const m = s.match(/verify-ticket\/([^/?#]+)/i); return (m ? m[1] : s).toUpperCase(); };
  const onScan = (text) => { const id = parseTid(text); setTid(id); setScanning(false); check(id); };

  return (
    <div className="max-w-md">
      <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-[#E6197E]" /> Gate Check-In</h3>
      <p className="text-sm text-zinc-400 mt-1">Scan a ticket's QR code with your camera, or type the Ticket ID to admit an attendee.</p>

      <div className="mt-5">
        {scanning ? (
          <div>
            <QrScanner onResult={onScan} onClose={() => setScanning(false)} />
            <Button onClick={() => setScanning(false)} variant="outline" className="mt-3 w-full border-white/15 bg-transparent rounded-lg" data-testid="checkin-stop-scan-button">Stop Camera</Button>
          </div>
        ) : (
          <Button onClick={() => { setResult(null); setScanning(true); }} className="w-full bg-[#21B6E6] hover:bg-[#1a93bd] rounded-lg" data-testid="checkin-scan-button">
            <Camera className="w-4 h-4 mr-2" /> Scan QR with Camera
          </Button>
        )}
      </div>

      <div className="mt-4 flex gap-2">
        <Input value={tid} onChange={(e) => setTid(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && check()} placeholder="TKT-XXXXXXXX" className="bg-[#131519] border-white/10 font-mono-accent" data-testid="checkin-ticket-input" />
        <Button onClick={() => check()} disabled={busy} className="bg-[#E6197E] hover:bg-[#C4136B] rounded-lg shrink-0" data-testid="checkin-submit-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Check In"}
        </Button>
      </div>

      {result && (
        <div className={`mt-5 rounded-2xl border p-5 ${result.status === "ok" ? "border-emerald-500/40 bg-emerald-950/30" : result.status === "already" ? "border-amber-500/40 bg-amber-950/20" : "border-red-500/40 bg-red-950/30"}`} data-testid="checkin-result">
          {result.status === "notfound" ? <p className="text-red-300 flex items-center gap-2"><Ban className="w-4 h-4" /> Ticket not found</p> : (
            <>
              <div className={`text-sm font-bold ${result.status === "ok" ? "text-emerald-400" : "text-amber-400"}`}>{result.status === "ok" ? "✓ Admitted" : "Already checked in"}</div>
              <div className="mt-2 font-heading text-lg">{result.name}</div>
              <div className="text-sm text-zinc-400">{result.event_title}</div>
              <div className="font-mono-accent text-xs text-[#E6197E] mt-1">{result.ticket_id}</div>
              {result.checked_in_at && <div className="text-[11px] text-zinc-500 mt-2">Checked in: {new Date(result.checked_in_at).toLocaleString()}</div>}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ExcoPanel({ isSuper }) {  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", scope: "national", lga: "", bio: "", photo: "", title: "", order: 0 });
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const excoFileRef = useRef(null);

  const uploadExcoPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, photo: data.path }));
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingPhoto(false); }
  };

  const roleOptions = form.scope === "national" ? [...NATIONAL_EXECUTIVES.map((n) => n.role), ...ALL_EXCO_ROLES] : ALL_EXCO_ROLES;
  const [customRole, setCustomRole] = useState("");

  const load = () => api.get("/exco").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const role = customRole.trim() || form.role;
    if (!form.name || !role) return toast.error("Name and role required");
    if (form.scope === "lga" && !form.lga) return toast.error("Select LGA");
    try {
      await api.post("/exco", { ...form, role });
      toast.success("Leader added");
      setOpen(false); setForm({ name: "", role: "", scope: "national", lga: "", bio: "", photo: "", title: "", order: 0 }); setCustomRole("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/exco/${id}`); toast.success("Removed"); load(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <div><h3 className="font-heading text-lg font-semibold">Leadership Directory</h3><p className="text-sm text-zinc-400 mt-1">Assign people & photos to national and LGA positions.</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="admin-add-leader-button"><Plus className="w-4 h-4 mr-2" /> Add Leader</Button></DialogTrigger>
          <DialogContent className="bg-[#131519] border-white/10 max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Add Leader</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="leader-name-input" /></div>
              <div><Label>Scope</Label>
              {isSuper ? (
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, role: "" })}>
                  <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="national">National</SelectItem><SelectItem value="lga">LGA</SelectItem></SelectContent>
                </Select>
              ) : <div className="mt-2 text-sm text-zinc-400">National leadership</div>}
              </div>
              {isSuper && form.scope === "lga" && <div><Label>LGA</Label>
                <Select value={form.lga} onValueChange={(v) => setForm({ ...form, lga: v })}>
                  <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10"><SelectValue placeholder="Select LGA" /></SelectTrigger>
                  <SelectContent>{LGAS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select></div>}
              <div><Label>Position / Role</Label>
                <Select value={form.role} onValueChange={(v) => { setForm({ ...form, role: v }); setCustomRole(""); }}>
                  <SelectTrigger className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="leader-role-select"><SelectValue placeholder="Select an existing role" /></SelectTrigger>
                  <SelectContent className="max-h-64">{roleOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
                <Input value={customRole} onChange={(e) => setCustomRole(e.target.value)} placeholder="…or type a brand-new role" className="mt-2 bg-[#0B0C0E] border-white/10" data-testid="leader-custom-role-input" />
                <p className="mt-1 text-[11px] text-zinc-500">Pick an existing position or add a new one. New roles show under "Additional Roles" on the public page.</p>
              </div>
              <div>
                <Label>Leader Photo</Label>
                <input ref={excoFileRef} type="file" accept="image/*" className="hidden" onChange={uploadExcoPhoto} data-testid="leader-photo-input" />
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-[#0B0C0E] border border-white/10 overflow-hidden flex items-center justify-center shrink-0">
                    {form.photo ? <img src={mediaUrl(form.photo)} alt="" className="w-full h-full object-cover" /> : <User className="w-6 h-6 text-zinc-600" />}
                  </div>
                  <Button type="button" variant="outline" className="border-white/15 bg-transparent" disabled={uploadingPhoto} onClick={() => excoFileRef.current?.click()} data-testid="leader-photo-upload-button">
                    {uploadingPhoto ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} {form.photo ? "Change Photo" : "Upload Photo"}
                  </Button>
                </div>
              </div>
              <div><Label>Bio (optional)</Label><Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-2 bg-[#0B0C0E] border-white/10" /></div>
              <Button onClick={create} className="w-full bg-[#E6197E] hover:bg-[#C4136B] rounded-full" data-testid="leader-save-button">Add Leader</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 ? <p className="text-zinc-500">No leaders added yet. Positions show as "open" on the public page.</p> : items.map((it) => (
          <div key={it.id} className="rounded-2xl border border-white/10 bg-[#131519] p-4 flex items-start gap-3">
            <div className="w-14 h-14 rounded-lg bg-zinc-800 overflow-hidden shrink-0">{it.photo && <img src={mediaUrl(it.photo)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0"><div className="font-heading font-semibold text-sm truncate">{it.name}</div><div className="text-xs text-[#E6197E]">{it.role}</div><div className="text-[11px] text-zinc-500">{it.scope === "national" ? "National" : it.lga}</div></div>
            <button onClick={() => del(it.id)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
